import { config } from '@abyss/web/tools/config';
import { t } from 'i18next';
import { find } from 'lodash';
import React, { useContext, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Benefits } from '../../../../../api/src/models/ProviderRecommendationDetails';
import { adobeLinkTrackEvent } from '../../../common/AdobeTagging/adobeLinkTrackEvent';
import { ReverseCoverageTypesCodes } from '../../../common/Constants';
import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import { PSXIcon } from '../../../common/PSXIcon';
import { isUmrMember } from '../../../common/Utils/memberUtils/memberUtils';
import { PortalContext } from '../../../context/PortalContext';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../hooks/useLagoon';
import { useSmartChoice } from '../../../hooks/useSmartChoice';
import { StoreKeys } from '../../../hooks/useStore/state';
import { useStore } from '../../../hooks/useStore/useStore';
import {
  ProviderDetails,
  ProviderLocation,
} from '../../../models/ProviderDetails';
import {
  RecommendationCriteriaMatch,
  countAllMeasures,
  getMatchCount,
  getMatchDescription,
  getMatchText,
} from '../../../utils/smartChoice.utils';
import {
  getCurrentMember,
  getMarketType,
  getProductCodeForCoverageType,
} from '../../../utils/user.utils';
import { adobeModalTrackEvent } from '../../AdobeTagging/adobeModalTrackEvent';
import {
  defaultStringToEmpty,
  getAcoBenefitPlan,
} from '../../Widget/SmartChoiceWidget/utils';
import { MatchText } from '../MatchText';
import { NotAMatchText } from '../NotAMatchText';
import {
  BreakdownContainer,
  CriteriaDescription,
  CriteriaPointsContainer,
  CriteriaSubTitle,
  CriteriaTitle,
  DescriptionContainer,
  DrawerContentWithBackgroundgray,
  FAQContainerWithOutTopBorder,
  FAQLink,
  StyledDescription,
} from '../SmartChoiceBreakDown.styles';
import { MatchTextWithTierLocations } from './Components/MatchTextWithTierLocations';
import { NotMatchTextWithTierLocations } from './Components/NotMatchTextWithTierLocations';
import {
  Maybe,
  acoProviderLocations,
  defaultBooleanToFalse,
  tieredProviderLocations,
} from './Components/utils';

interface BenifitsBreakDownProps {
  benefits: Benefits;
  acoName: string;
  providerDetails: ProviderDetails;
  selectedLocatoionCity: string;
  providerLocationData: ProviderLocation;
  handleTabChange: (tab: string | number) => void;
  isCareAvl?: boolean;
}
const getIsCostIndicator = (
  tired: boolean,
  aco: boolean,
  indicator: Maybe<string>
): boolean =>
  !tired &&
  !aco &&
  (indicator === '$' || indicator === '$$' || indicator === '$$$');

const getTieredCount = (
  isTieredPlan: boolean,
  isProviderTierDesignation: string,
  isAcoBenefitPlan: boolean,
  isAcoParticipationStatus: boolean
): number => {
  let count = 0;
  if (
    isTieredPlan &&
    (isProviderTierDesignation === 'y' || isProviderTierDesignation === 'n')
  ) {
    count += 1;
  }
  if (
    isAcoBenefitPlan &&
    (isAcoParticipationStatus || !isAcoParticipationStatus)
  ) {
    count += 1;
  }
  return count;
};

export const BenifitsBreakDown = ({
  benefits,
  acoName,
  providerDetails,
  providerLocationData,
  selectedLocatoionCity,
  handleTabChange,
  isCareAvl,
}: BenifitsBreakDownProps) => {
  const { portalData } = useContext(PortalContext);
  const [searchParams] = useSearchParams();
  const isWidget = useStore(StoreKeys?.IS_WIDGET);

  const selectedMember = getCurrentMember();
  const isUmr = isUmrMember(portalData?.portalName);
  const baseUrl = isUmr ? config('UMR_BASE_URL') : config('BASE_URL');
  const isTieredPlanSearchParam = searchParams.get('isTieredPlan');
  const providerData = providerDetails;
  const providerLocations = providerData?.providerLocations;

  const featureSuppression = useSmartChoice();
  const isTieredPlan = isTieredPlanSearchParam === 'true' && featureSuppression;
  const tieredProviderLocation = tieredProviderLocations(providerLocations);

  const acoProvider = providerLocationData?.acoProvider ?? [];
  const acoProviderLocation = acoProviderLocations(providerLocations);
  const providerCostIndicator = benefits?.costIndicator;
  const providerCostEfficiencyCode = providerLocationData?.superPremium ?? '';
  const isPreferredProviderStatus: boolean =
    providerLocationData?.isGreenDiamondProvider ?? false;
  const isProviderTierDesignation: Maybe<String> =
    benefits?.providerTierDesignation?.toLowerCase();

  const isAcoParticipationStatus: boolean =
    benefits?.acoParticipationStatus === 'Y';
  const isACOProvider =
    acoProvider?.length > 0 && !defaultBooleanToFalse(featureSuppression);
  const isCostIndicator = getIsCostIndicator(
    isTieredPlan,
    isACOProvider,
    providerCostIndicator
  );

  useEffect(() => {
    adobeModalTrackEvent('Smart choice benefits breakdown');
  }, []);

  const allConfigs = useLagoon('config')();
  const [enableViewCostLink] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_VIEW_COST_LINK,
  ]);

  const isCostEfficiencyRating =
    providerCostEfficiencyCode !== '' &&
    benefits?.costEfficiencyRating !== 'NA';
  const isCostEfficiencyCountAvl =
    isCostEfficiencyRating &&
    benefits?.costEfficiencyRating !== 'F' &&
    benefits?.costEfficiencyRating !== 'G';
  const acoBenefitPlanCollection = allConfigs?.find(
    (row) => row.key === 'ACO_BENEFIT_COLLECTION'
  )?.value;
  const isAcoBenefitPlan = getAcoBenefitPlan(
    defaultStringToEmpty(
      getProductCodeForCoverageType(
        selectedMember,
        ReverseCoverageTypesCodes.MEDICAL
      )
    ),
    defaultStringToEmpty(getMarketType(selectedMember)),
    defaultStringToEmpty(selectedMember?.membershipCategory),
    defaultStringToEmpty(selectedMember?.population),
    acoBenefitPlanCollection
  );

  const inNetworkStatus = true;
  const count = getMatchCount(
    isPreferredProviderStatus,
    isTieredPlan &&
      (isProviderTierDesignation === 'y' || isProviderTierDesignation === 'n'),
    isAcoParticipationStatus,
    isCostIndicator,
    isCostEfficiencyCountAvl,
    inNetworkStatus
  );

  const totalCount = countAllMeasures(
    isPreferredProviderStatus
      ? 1
      : getTieredCount(
          isTieredPlan,
          isProviderTierDesignation,
          isAcoBenefitPlan,
          isAcoParticipationStatus
        ),
    isCostIndicator,
    inNetworkStatus,
    isCostEfficiencyRating
  );

  const replacePlaceholders = (
    text: string | undefined,
    replacements: Record<string, string>
  ): string =>
    (text || '').replace(/\{\{(.*?)}}/g, (_, key) => {
      const keys = key.split('.');
      let value: any = replacements;
      for (const k of keys) {
        value = value?.[k];
        if (value === undefined) break;
      }
      return typeof value === 'string' ? value : '';
    });
  const lagoonData = useLagoon('ui-messaging')();
  const preferredLocationSubtitle = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_PREFERRED_PROVIDER_AND_LOCATION_SUB_TITLE,
  });
  const description = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_DESCRIPTION,
  });
  const in_network_provider = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_IN_NETWORK,
  });
  const preferred_provider = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_PREFERRED_PROVIDER,
  });
  const provider_tier = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_PROVIDER_TIER,
  });
  const preferred_tier1_and_loactions = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_PROVIDER_TIER1_WITH_LOCATION,
  });
  const not_prefered_tier1_and_loactions = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_PROVIDER_ISNOT_TIER1_WITH_LOCATION,
  });
  const provider_participated_aco = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_PROVIDER_PARTICIPATES_ACO_NAME,
  });
  const provider_participates_aco_name_and_location = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_PROVIDER_PRATICIPATES_ACO_NAME_AND_LOCATION,
  });
  const provider_does_not_participate_aco = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_DOES_NOT_PARTICIPATES_ELIGIBLE_AT_ACO_NAME,
  });
  const provider_does_not_participate_with_location = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_PROVIDER_NOT_PARTICIPATES_ACO_NAME_AND_LOCATION,
  });
  const costSubtitle = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_COST_SUB_TITLE,
  });
  const below_average_cost_indicator = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_BELOW_AVARAGE_COST_FOR_OFFICE_VISIT,
  });
  const average_cost_indicator = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_AVERAGE_COST_FOR_OFFICE_VISIT,
  });
  const above_average_cost_indicator = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_ABOVE_AVERAGE_COST_FOR_OFFICE_VISIT,
  });
  const below_average_cost_of_care = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_BELOW_AVARAGE_TOTAL_COST_OF_CARE,
  });
  const average_cost_of_care = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_AVERAGE_TOTAL_COST_OF_CARE,
  });
  const above_average_cosst_of_care = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_ABOVE_AVERAGE_TOTAL_COST_OF_CARE,
  });
  const all_criteria_met = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_QUALITY_BREAKDOWN
      .QUALITY_BREAKDOWN_ALL_CRITERIA_MET_TITLE_AND_DESCRIPTION,
  });
  const FAQs = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BREAKDOWN_FAQ_TEXT,
  });

  const outOf = t('OUT_OF');
  const outOfTotal = getMatchText(
    benefits?.scoreDetails?.match as string,
    `(${count} ${outOf} ${totalCount})`
  );

  return (
    <BreakdownContainer data-testid="benefits-breakdown">
      <DrawerContentWithBackgroundgray direction="column">
        <CriteriaTitle data-testid="match-description">
          {`${t(
            getMatchDescription(
              benefits?.scoreDetails?.match ?? RecommendationCriteriaMatch.None
            )
          )} ${outOfTotal}`}
        </CriteriaTitle>
        <CriteriaDescription data-testid="criteria-met-description">
          {all_criteria_met?.message}
        </CriteriaDescription>
      </DrawerContentWithBackgroundgray>
      <DescriptionContainer data-testid="benefits-breakdown-description">
        <StyledDescription>{description?.message}</StyledDescription>
      </DescriptionContainer>
      <CriteriaPointsContainer
        data-testid="benefits-breakdown-criteria"
        direction="column"
      >
        {!isWidget && enableViewCostLink && (
          <CriteriaSubTitle>
            {preferredLocationSubtitle?.message}
          </CriteriaSubTitle>
        )}
        <MatchText text={in_network_provider?.message} />
        {isPreferredProviderStatus ? (
          <MatchText
            StartIcon={<PSXIcon icon={'green_diamond'} size={24} />}
            text={preferred_provider?.message}
          />
        ) : (
          <>
            {isTieredPlan && (
              <>
                {isProviderTierDesignation === 'y' ? (
                  tieredProviderLocation.length > 1 ? (
                    <MatchTextWithTierLocations
                      StartIcon={<PSXIcon icon="Group 204976975" size={24} />}
                      handleTabChange={handleTabChange}
                      isViewLink={!isWidget}
                      text={replacePlaceholders(
                        preferred_tier1_and_loactions?.message,
                        {
                          selectedLocatoionCity,
                        }
                      )}
                    />
                  ) : (
                    <MatchText
                      StartIcon={<PSXIcon icon="Group 204976975" size={24} />}
                      text={provider_tier?.message}
                    />
                  )
                ) : (
                  isProviderTierDesignation === 'n' && (
                    <>
                      {tieredProviderLocation.length >= 1 ? (
                        <NotMatchTextWithTierLocations
                          handleTabChange={handleTabChange}
                          isViewLink={!isWidget}
                          text={replacePlaceholders(
                            not_prefered_tier1_and_loactions?.message,
                            { selectedLocatoionCity }
                          )}
                        />
                      ) : (
                        <NotAMatchText text={provider_tier?.message} />
                      )}
                    </>
                  )
                )}
              </>
            )}
            {isAcoBenefitPlan && (
              <>
                {isAcoParticipationStatus && acoProvider?.length ? (
                  acoProviderLocation.length > 1 ? (
                    <MatchText
                      text={replacePlaceholders(
                        provider_participates_aco_name_and_location?.message,
                        { selectedLocatoionCity, acoName }
                      )}
                    />
                  ) : (
                    <MatchText
                      text={replacePlaceholders(
                        provider_participated_aco?.message,
                        {
                          acoName,
                        }
                      )}
                    />
                  )
                ) : (
                  !isAcoParticipationStatus && (
                    <>
                      {acoProviderLocation?.length > 1 ? (
                        <NotAMatchText
                          text={replacePlaceholders(
                            provider_does_not_participate_with_location?.message,
                            { selectedLocatoionCity }
                          )}
                        />
                      ) : (
                        <NotAMatchText
                          text={provider_does_not_participate_aco?.message}
                        />
                      )}
                    </>
                  )
                )}
              </>
            )}
          </>
        )}
        {!isWidget && enableViewCostLink && (
          <CriteriaSubTitle>{costSubtitle?.message}</CriteriaSubTitle>
        )}
        {isCostIndicator && (
          <React.Fragment>
            {benefits?.costIndicator === '$' && (
              <MatchText text={below_average_cost_indicator?.message} />
            )}
            {benefits?.costIndicator === '$$' && (
              <MatchText text={average_cost_indicator?.message} />
            )}
            {benefits?.costIndicator === '$$$' && (
              <NotAMatchText text={above_average_cost_indicator?.message} />
            )}
          </React.Fragment>
        )}
        {benefits?.costEfficiencyRating !== 'NA' && (
          <>
            {benefits?.costEfficiencyRating &&
              (() => {
                switch (benefits?.costEfficiencyRating) {
                  case 'A':
                  case 'B':
                  case 'C':
                  case 'D':
                    return (
                      isCostEfficiencyRating && (
                        <MatchTextWithTierLocations
                          handleTabChange={handleTabChange}
                          isCost
                          isViewLink={
                            !isWidget && isCareAvl && enableViewCostLink
                          }
                          text={below_average_cost_of_care?.message}
                        />
                      )
                    );
                  case 'E':
                    return (
                      <MatchTextWithTierLocations
                        handleTabChange={handleTabChange}
                        isCost
                        isViewLink={
                          !isWidget && isCareAvl && enableViewCostLink
                        }
                        text={average_cost_of_care?.message}
                      />
                    );
                  case 'F':
                  case 'G':
                    return (
                      <NotMatchTextWithTierLocations
                        handleTabChange={handleTabChange}
                        isCost
                        isViewLink={
                          !isWidget && isCareAvl && enableViewCostLink
                        }
                        text={above_average_cosst_of_care?.message}
                      />
                    );
                  default:
                    return null;
                }
              })()}
          </>
        )}
      </CriteriaPointsContainer>

      <FAQContainerWithOutTopBorder>
        <FAQLink
          data-testid="faq-link"
          href={`${baseUrl}/findcare/faq${isWidget ? '?source=cex' : ''}`}
          onClick={() => {
            adobeLinkTrackEvent({
              name: 'Faqs',
              location: 'Smart choice benefits breakdown',
            });
          }}
        >
          {FAQs?.message}
        </FAQLink>
      </FAQContainerWithOutTopBorder>
    </BreakdownContainer>
  );
};
